import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Escudo, Mas, Reloj, Sobre } from '../componentes/Iconos';
import { AvisoError, CabeceraMarca, CabeceraPaso } from '../componentes/Piezas';
import { api, mensajeDeError } from '../lib/api';
import { leerLocal, recordarLocal } from '../lib/ganchos';

const CORREO_VALIDO = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const CLAVE_CORREO = 'mentorking:ultimoCorreo';
/** Espera antes de reenviar: el correo tarda, y apretar diez veces no lo acelera. */
const ESPERA_REENVIO_S = 60;

/** "Busca tu sesión": se pide el enlace por correo. Sin cuenta ni contraseña. */
export function Acceso() {
  const [params] = useSearchParams();
  const navegar = useNavigate();
  const [correo, setCorreo] = useState(() => params.get('correo') ?? leerLocal(CLAVE_CORREO));
  const [tocado, setTocado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string>();
  const valido = CORREO_VALIDO.test(correo.trim());

  async function enviar(ev: FormEvent) {
    ev.preventDefault();
    setTocado(true);
    if (!valido || enviando) return;
    setEnviando(true);
    setError(undefined);
    try {
      await api.acceso(correo.trim());
      recordarLocal(CLAVE_CORREO, correo.trim());
      navegar('/revisa', { state: { correo: correo.trim(), enviadoEn: Date.now() } });
    } catch (e) {
      setError(mensajeDeError(e));
      setEnviando(false);
    }
  }

  return (
    <div className="pantalla">
      <CabeceraMarca accion={{ a: '/', texto: 'Reservar' }} />
      <form className="contenido" onSubmit={enviar} noValidate style={{ paddingTop: 36, gap: 22 }}>
        <div className="icono-redondo">
          <Sobre />
        </div>
        <div>
          <h1 className="titulo">Busca tu sesión</h1>
          <p className="parrafo" style={{ marginTop: 10 }}>
            Escribe el correo con el que reservaste y te enviamos el enlace para ver, mover o entrar a tu sesión.
          </p>
        </div>

        <div className="campo">
          <label htmlFor="correo" className="campo__etiqueta campo__etiqueta--chica">
            Tu correo
          </label>
          <input
            id="correo"
            className="campo__entrada"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="tu@correo.com"
            value={correo}
            onChange={(e) => setCorreo(e.target.value)}
            onBlur={() => setTocado(true)}
            aria-invalid={tocado && !valido}
            aria-describedby={tocado && !valido ? 'correo-error' : undefined}
          />
          {tocado && !valido && (
            <span id="correo-error" className="campo__error">
              Revisa el correo: parece incompleto.
            </span>
          )}
        </div>

        {error && <AvisoError>{error}</AvisoError>}

        <button type="submit" className="boton" disabled={enviando}>
          {enviando ? 'Enviando…' : 'Enviarme el enlace'}
        </button>

        <div className="aviso">
          <Escudo />
          <span>Busca el correo de confirmación que te llegó al reservar: ese enlace también te lleva directo.</span>
        </div>
      </form>

      <div className="pie">
        <Link to="/" className="boton boton--secundario">
          <Mas />
          Aún no reservo, quiero un día
        </Link>
        <p className="nota" style={{ textAlign: 'center' }}>
          Sin cuenta ni contraseña.
        </p>
      </div>
    </div>
  );
}

/** "Revisa tu correo". La respuesta es la misma exista o no el correo: no revela quién es cliente. */
export function Revisa() {
  const { state } = useLocation() as { state?: { correo?: string; enviadoEn?: number } };
  const navegar = useNavigate();
  const correo = state?.correo;
  const [enviadoEn, setEnviadoEn] = useState(state?.enviadoEn ?? Date.now());
  const [ahora, setAhora] = useState(Date.now());
  const [error, setError] = useState<string>();
  const [reenviado, setReenviado] = useState(false);

  useEffect(() => {
    if (!correo) navegar('/acceso', { replace: true });
  }, [correo, navegar]);

  useEffect(() => {
    const id = window.setInterval(() => setAhora(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const faltan = Math.max(0, ESPERA_REENVIO_S - Math.floor((ahora - enviadoEn) / 1000));

  async function reenviar() {
    if (!correo || faltan > 0) return;
    setError(undefined);
    try {
      await api.acceso(correo);
      setEnviadoEn(Date.now());
      setReenviado(true);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  return (
    <div className="pantalla">
      <CabeceraPaso volverA="/acceso" volverTexto="Volver a escribir el correo" titulo="Busca tu sesión" />
      <div className="contenido" style={{ paddingTop: 32, gap: 20 }}>
        <div className="icono-redondo">
          <Sobre tam={27} />
        </div>
        <div>
          <h1 className="titulo">Revisa tu correo</h1>
          <p className="parrafo" style={{ marginTop: 11 }}>
            Si <span className="fuerte">{correo}</span> tiene sesiones, ahí está el enlace para verlas.
          </p>
        </div>

        <div className="tarjeta">
          <div style={{ display: 'flex', gap: 10 }}>
            <Reloj />
            <p className="parrafo" style={{ fontSize: 13 }}>
              El enlace vence en <span className="fuerte">7 días</span>.
            </p>
          </div>
          <hr className="separador" />
          <div style={{ display: 'flex', gap: 10 }}>
            <Escudo />
            <p className="parrafo" style={{ fontSize: 13 }}>
              Los enlaces que te enviamos antes siguen funcionando. Este no los anula.
            </p>
          </div>
        </div>

        <div className="campo">
          <span className="fuerte" style={{ fontSize: 14 }}>
            ¿No te llegó?
          </span>
          <p className="parrafo" style={{ fontSize: 13 }}>
            Puede tardar un par de minutos. Revisa la carpeta de spam antes de volver a pedirlo.
          </p>
          {error && <AvisoError>{error}</AvisoError>}
          <button type="button" className="boton boton--secundario" disabled={faltan > 0} onClick={reenviar} aria-live="polite">
            {faltan > 0 ? `Reenviar en 0:${String(faltan).padStart(2, '0')}` : reenviado ? 'Reenviar otra vez' : 'Reenviar el enlace'}
          </button>
        </div>
      </div>

      <div className="pie">
        <Link to="/acceso" className="enlace-texto">
          Usar otro correo
        </Link>
      </div>
    </div>
  );
}
